import { AsyncPipe, UpperCasePipe } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxIconBoxComponent,
  OnyxIconComponent,
  OnyxLanguagePipe,
  OnyxToggleComponent,
} from '@onyx/angular';
import { isEqual } from 'lodash';
import { combineLatestWith, distinctUntilChanged, map, skip } from 'rxjs';
import { validate as validateUuid } from 'uuid';
import { OrderHelper } from '../../../../common/helpers/order.helper';
import type { OrderFormGood } from '../../../../common/interfaces/order-form-good';
import { OrderFormService } from '../../../../common/services/order-form.service';
import type { OrderUnloadingPointGoodFormGroup } from './order-unloading-point-good-form/order-unloading-point-good-form.component';
import { OrderUnloadingPointGoodFormComponent } from './order-unloading-point-good-form/order-unloading-point-good-form.component';

@Component({
  selector: 'app-order-unloading-point-form-good',
  imports: [
    TranslatePipe,
    OnyxIconBoxComponent,
    OnyxIconComponent,
    OnyxToggleComponent,
    UpperCasePipe,
    OrderUnloadingPointGoodFormComponent,
    AsyncPipe,
  ],
  templateUrl: './order-unloading-point-form-good.component.html',
  styleUrl: './order-unloading-point-form-good.component.scss',
})
export class OrderUnloadingPointFormGoodComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly languagePipe = inject(OnyxLanguagePipe);
  private readonly orderFormService = inject(OrderFormService);

  public readonly good = input.required<OrderFormGood>();
  public readonly form =
    input.required<OrderUnloadingPointGoodFormGroup | null>();

  public readonly add = output<void>();
  public readonly remove = output<void>();

  protected readonly I18N = 'orders.orderForm.points.unloadingPoint';

  private readonly goodTypes$ = this.orderFormService.goodTypes$;

  protected goodTypeName$ = toObservable(this.good).pipe(
    map((good) => good.good.type),
    distinctUntilChanged(),
    combineLatestWith(this.goodTypes$),
    map(([goodType, goodTypes]) => {
      if (!goodType) return null;
      if (!validateUuid(goodType)) return goodType;

      const type = goodTypes.find((type) => type.uuid === goodType);
      return type ? this.languagePipe.transform(type.names) : null;
    }),
    takeUntilDestroyed(this.destroyRef),
  );

  protected emptyQuantity = computed(
    () => !OrderHelper.getGoodQuantity(this.good().good),
  );

  constructor() {
    toObservable(this.good)
      .pipe(
        map((good) => good.good.size.type),
        distinctUntilChanged(isEqual),
        skip(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.form()?.controls.quantity.setValue(null));
  }
}
